import React, { useState, useEffect } from 'react';
import DashboardContainer from '../../components/dashboard/DashboardContainer';
import notificationService from '../../services/notificationService';
import toast from 'react-hot-toast';
import api, { API_URL } from '../../api';
import '../../assets/css/pages/settings/settings.css';
import { hasRole } from '../../services/userPosition';
import {
    UserIcon,
    Notification02Icon,
    SchoolBell01Icon,
} from 'hugeicons-react';

const SettingsPageContent = () => {
    const [activeTab, setActiveTab] = useState('notifications');
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState();
    const [filterEnabled, setFilterEnabled] = useState('all'); 
    const [subscriptionChoice, setSubscriptionChoice] = useState('all'); 
    const [categoriesError, setCategoriesError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);

    useEffect(() => {
        if (activeTab === 'notifications') {
            if (!categoriesLoaded) {
                fetchCategories();
            }
        } else if (activeTab === 'account') {
            fetchUserProfile();
        }
    }, [activeTab, categoriesLoaded]);

    const fetchCategories = async (isRetry = false) => {
        try {
            setLoading(true);
            setCategoriesError(null);

            const categoriesData = await notificationService.getCategories();

            // Get local subscriptions
            const localSubscriptions = notificationService.getLocalSubscriptions();

            // Merge fetched categories with local subscriptions
            const mergedCategories = categoriesData.map(cat => ({
                ...cat,
                is_subscribed: localSubscriptions[cat.category] !== undefined ? localSubscriptions[cat.category] : cat.is_subscribed
            }));

            // Set "Measure Data" to enabled by default if not set
            const updatedCategories = mergedCategories.map(cat => {
                if (cat.category === 'Measure Data' && localSubscriptions[cat.category] === undefined) {
                    return { ...cat, is_subscribed: true };
                }
                return cat;
            });

            setCategories(updatedCategories);
            setCategoriesLoaded(true);
            setRetryCount(0);
        } catch (error) {
            console.error('Error fetching categories:', error);
            console.error('Error details:', error.response?.data, error.response?.status, error.message);

            setCategories([]);

            // Store error details for retry functionality
            setCategoriesError(error);

            // Show detailed error message based on error type
            if (!isRetry) {
                if (error.response?.status === 401) {
                    toast.error('Authentication required. Please log in again.');
                } else if (error.response?.status === 403) {
                    toast.error('You don\'t have permission to access notification settings.');
                } else if (error.response?.status === 404) {
                    toast.error('Notification categories endpoint not found. Please contact support.');
                } else if (error.response?.status >= 500) {
                    toast.error('Server error occurred. Please try again later.');
                } else if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
                    toast.error('Network connection issue. Please check your internet connection.');
                } else {
                    toast.error('Failed to load notification categories. Please refresh the page or try again later.');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const retryFetchCategories = () => {
        setRetryCount(prev => prev + 1);
        fetchCategories(true);
    };

    const fetchUserProfile = async () => {
        try {
            setProfileLoading(true);
            // Try to get user profile from API
            const response = await api.get(`${API_URL}/user/profile/`);
            setUserProfile(response.data);
        } catch (error) {
            console.error('Error fetching user profile:', error);
          
        } finally {
            setProfileLoading(false);
        }
    };

    const handleToggleSubscription = async (categoryName, currentStatus) => {
        try {
            setUpdating(true);
            const newStatus = !currentStatus;

            await notificationService.updateSubscription(categoryName, newStatus);

            // Update local state
            setCategories(prev =>
                prev.map(cat =>
                    cat.category === categoryName
                        ? { ...cat, is_subscribed: newStatus }
                        : cat
                )
            );

            toast.success(`Notifications ${newStatus ? 'enabled' : 'disabled'} for ${categoryName}`);
        } catch (error) {
            toast.error('Failed to update notification preference');
            console.error('Error updating subscription:', error);
        } finally {
            setUpdating(false);
        }
    };

    const handleBulkToggle = async (enableAll) => {
        try {
            setUpdating(true);

            // Update all categories to the same status
            const updatedCategories = categories.map(cat => ({
                category: cat.category,
                is_subscribed: enableAll
            }));

            await notificationService.bulkUpdateSubscriptions(updatedCategories);

            // Update local state
            setCategories(prev =>
                prev.map(cat => ({
                    ...cat,
                    is_subscribed: enableAll
                }))
            );

            toast.success(`All notifications ${enableAll ? 'enabled' : 'disabled'} successfully!`);
        } catch (error) {
            toast.error(`Failed to ${enableAll ? 'enable' : 'disable'} all notifications`);
            console.error('Error bulk updating subscriptions:', error);
        } finally {
            setUpdating(false);
        }
    };

    const formatCategoryName = (category) => {
        return category.replace(/\s+/g, ' ').trim();
    };
    // Generate dynamic filter options based on actual categories from database
    const getFilterOptions = () => {
        if (!Array.isArray(categories) || categories.length === 0) {
            return [];
        }

        // Create options for each unique category directly from database
        const uniqueCategories = categories.map(category => ({
            label: formatCategoryName(category.category),
            value: category.category
        }));

        // Remove duplicates if any and sort alphabetically
        const uniqueOptions = uniqueCategories.filter((option, index, self) => 
            index === self.findIndex(o => o.value === option.value)
        );

        return uniqueOptions.sort((a, b) => a.label.localeCompare(b.label));
    };

    const filterOptions = getFilterOptions();

    const filteredCategories = (Array.isArray(categories) ? categories : []).filter(category => {
        // Filter by selected specific category
        if (selectedCategory && selectedCategory !== '' && selectedCategory !== 'all') {
            if (category.category !== selectedCategory) return false;
        }

        // Filter by enabled/disabled status
        if (filterEnabled === 'enabled' && !category.is_subscribed) return false;
        if (filterEnabled === 'disabled' && category.is_subscribed) return false;

        // Filter by subscription choice
        if (subscriptionChoice === 'subscribed' && !category.is_subscribed) return false;
        if (subscriptionChoice === 'unsubscribed' && category.is_subscribed) return false;

        return true;
    });

    const tabs = [
        { id: 'notifications', label: 'Notifications', icon: SchoolBell01Icon },
        { id: 'account', label: 'Account Profile', icon: UserIcon },
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'notifications':
                return (
                    <div className="settings-card">
                        <div className="card-header">
                            <Notification02Icon size={24} className="card-icon" />
                            <h2 className="card-title">Notification Preferences</h2>
                        </div>
                        <div className='filters'>
                            <p>
                                Choose which notifications you want to receive. Changes are saved automatically.
                                {loading && <span >Loading...</span>}
                            </p>

                            {/* Filter Controls */}
                            <div className="filter-controls">
                                <div className="filter-group">
                                    <label className="filter-label">
                                        Filter by Category:
                                    </label>
                                    <select
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        <option value="">All Categories</option>
                                        {filterOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className='filter-group'>
                                    <label>
                                        Filter by Status:
                                    </label>
                                    <select
                                        value={filterEnabled}
                                        onChange={(e) => setFilterEnabled(e.target.value)}
                                    
                                    >
                                        <option value="all">All Status</option>
                                        <option value="enabled">Enabled Only</option>
                                        <option value="disabled">Disabled Only</option>
                                    </select>
                                </div>

                                <div className='filter-group'>
                                    <label>
                                        Show Subscriptions:
                                    </label>
                                    <select
                                        value={subscriptionChoice}
                                        onChange={(e) => setSubscriptionChoice(e.target.value)}
                                      
                                    >
                                        <option value="all">All Categories</option>
                                        <option value="subscribed">Subscribed Only</option>
                                        <option value="unsubscribed">Not Subscribed</option>
                                    </select>
                                </div>

                                {(selectedCategory || filterEnabled !== 'all' || subscriptionChoice !== 'all') && (
                                    <button
                                        onClick={() => {
                                            setSelectedCategory('');
                                            setFilterEnabled('all');
                                            setSubscriptionChoice('all');
                                        }}
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading">Loading notification categories...</div>
                        ) : categoriesError ? (
                            <div className="error-state">
                                <p>Failed to load notification categories</p>
                                <button onClick={retryFetchCategories} disabled={loading}>
                                    {loading ? 'Retrying...' : 'Retry'}
                                </button>
                            </div>
                        ) : Array.isArray(categories) && categories.length > 0 ? (
                            <div className='filtered-category'>
                                <p className='showed-category'>
                                    Showing <strong>{filteredCategories.length}</strong> of <strong>{categories.length}</strong> categories
                                    {(selectedCategory || filterEnabled !== 'all' || subscriptionChoice !== 'all') && (
                                        <span>
                                            {selectedCategory && ` for "${formatCategoryName(selectedCategory)}" category`}
                                            {selectedCategory && (filterEnabled !== 'all' || subscriptionChoice !== 'all') && ', '}
                                            {filterEnabled !== 'all' && `status: ${filterEnabled}`}
                                            {filterEnabled !== 'all' && subscriptionChoice !== 'all' && ', '}
                                            {subscriptionChoice !== 'all' && `subscriptions: ${subscriptionChoice}`}
                                        </span>
                                    )}
                                </p>

                                {filteredCategories.length > 0 ? (
                                    <>
                                        {filteredCategories.map((category) => (
                                            <div key={category.category} className="notification-item">
                                                <div className="notification-info">
                                                    <h3 className="notification-name">{formatCategoryName(category.category)}</h3>
                                                    <p className="notification-desc">
                                                        Receive notifications for {formatCategoryName(category.category).toLowerCase()} related activities
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className={`status-badge ${category.is_subscribed ? 'status-enabled' : 'status-disabled'}`}>
                                                        {category.is_subscribed ? 'Enabled' : 'Disabled'}
                                                    </span>
                                                    <label className="toggle-switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={category.is_subscribed}
                                                            onChange={() => handleToggleSubscription(category.category, category.is_subscribed)}
                                                            disabled={updating}
                                                        />
                                                        <span className="slider"></span>
                                                    </label>
                                                </div>
                                            </div>
                                        ))}

                                        <div className='actions'>
                                            <button
                                                onClick={() => handleBulkToggle(true)}
                                                disabled={updating}
                                            >
                                                <SchoolBell01Icon size={16} />
                                                {updating ? 'Updating...' : 'Enable All'}
                                            </button>
                                            <button
                                                onClick={() => handleBulkToggle(false)}
                                                disabled={updating}
                                            >
                                                <Notification02Icon size={16} />
                                                {updating ? 'Updating...' : 'Disable All'}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="no-results">
                                        <p>No categories match your current filters</p>
                                        <p>Try adjusting your category or status filters to see more results</p>
                                        <button
                                            onClick={() => {
                                                setSelectedCategory('');
                                                setFilterEnabled('all');
                                                setSubscriptionChoice('all');
                                            }}
                                        >
                                            Clear All Filters
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <p>No notification categories available</p>
                                <p>Please contact your administrator if you need access to notification settings.</p>
                                <button onClick={() => fetchCategories()} disabled={loading}>
                                    Refresh
                                </button>
                            </div>
                        )}
                    </div>
                );

            case 'account':
                return (
                    <div className="settings-card">
                        <div className="card-header">
                            <UserIcon size={24} className="card-icon" />
                            <h2 className="card-title">Account Profile</h2>
                        </div>

                        {profileLoading ? (
                            <div className="loading">Loading profile information...</div>
                        ) : userProfile ? (
                            <div className="profile-section">
                                <div className="field-group">
                                    <label>
                                        First Name
                                    </label>
                                    <div>
                                        {userProfile.first_name || 'Not provided'}
                                    </div>
                                </div>

                                <div className="field-group">
                                    <label>
                                        Last Name
                                    </label>
                                    <div>
                                        {userProfile.last_name || 'Not provided'}
                                    </div>
                                </div>

                                <div className="field-group">
                                    <label>
                                        Email Address
                                    </label>
                                    <div>
                                        {userProfile.email || 'Not provided'}
                                    </div>
                                </div>

                                <div className="field-group">
                                    <label>
                                        Hospital/Facility
                                    </label>
                                    <div>
                                        {userProfile.hospital || 'Not assigned'}
                                    </div>
                                </div>

                                <div className="field-group">
                                    <label>
                                        Position/Role
                                    </label>
                                    <div>
                                        {userProfile.position || 'Not assigned'}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="error-message">
                                <p>Unable to load profile information.</p>
                            </div>
                        )}
                    </div>
                );
        }
    };


    return (
        <div className="settings-modern">
         
            <div className="settings-tabs">
                {tabs.map((tab) => {
                    const IconComponent = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <IconComponent size={18}  />
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Tab Content */}
            {renderTabContent()}
        </div>
    );
};

const SettingsPage = () => {
    return (
        <DashboardContainer content={<SettingsPageContent />} pageTitle={'Settings'} />
    );
};

export default SettingsPage;